import { Injectable, PLATFORM_ID, computed, effect, inject, signal } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Player, RatingMode, TeamComposition, benchSurplus } from './player.model';

const KEY_PLAYERS = 'equifoot.v2.players';
const KEY_MODE = 'equifoot.v2.ratingMode';
const KEY_TEAM_SIZE = 'equifoot.v2.teamSize';
const LEGACY_KEY_PLAYERS = 'equifoot_players';
const LEGACY_KEY_MODE = 'equifoot_rating_system';

export interface RemovedPlayer {
  player: Player;
  index: number;
}

/**
 * Source de vérité unique de l'effectif. Persiste dans localStorage et migre
 * l'ancien format (notes stockées en 1-5 quand le mode étoiles était actif).
 */
@Injectable({ providedIn: 'root' })
export class RosterStore {
  private platformId = inject(PLATFORM_ID);
  private isBrowser = isPlatformBrowser(this.platformId);

  readonly players = signal<Player[]>([]);
  readonly ratingMode = signal<RatingMode>('SCORE');
  /** Joueurs par équipe. null = auto, l'effectif entier est réparti. */
  readonly teamSize = signal<number | null>(null);
  readonly composition = signal<TeamComposition | null>(null);
  /** L'effectif a changé depuis la dernière génération. */
  readonly compositionStale = signal(false);

  readonly activePlayers = computed(() => this.players().filter((p) => p.active));
  readonly activeCount = computed(() => this.activePlayers().length);

  /** Présents qui entrent réellement dans les équipes. */
  readonly playingPlayers = computed(() => this.activePlayers().filter((p) => !p.benched));
  /** Présents mis de côté à la main. */
  readonly benchedPlayers = computed(() => this.activePlayers().filter((p) => p.benched));

  /** Remplaçants qu'il reste à désigner avant de pouvoir générer. */
  readonly surplus = computed(() => benchSurplus(this.playingPlayers().length, this.teamSize()));

  /**
   * En dessous de deux joueurs, le blocage vient du manque d'effectif, pas du
   * banc: réclamer un remplaçant à quelqu'un qui n'a qu'un joueur n'a aucun sens.
   */
  readonly needsBench = computed(() => this.playingPlayers().length >= 2 && this.surplus() > 0);

  readonly canGenerate = computed(() => this.playingPlayers().length >= 2 && this.surplus() === 0);

  constructor() {
    this.hydrate();

    effect(() => {
      const players = this.players();
      const mode = this.ratingMode();
      const size = this.teamSize();
      if (!this.isBrowser) return;
      try {
        localStorage.setItem(KEY_PLAYERS, JSON.stringify(players));
        localStorage.setItem(KEY_MODE, mode);
        if (size === null) localStorage.removeItem(KEY_TEAM_SIZE);
        else localStorage.setItem(KEY_TEAM_SIZE, String(size));
      } catch {
        // Quota atteint ou stockage refusé: on continue sans persistance.
      }
    });
  }

  // ---------------------------------------------------------------- mutations

  addPlayer(name: string, rating: number): Player | null {
    const cleaned = name.trim().replace(/\s+/g, ' ');
    if (!cleaned) return null;

    const player: Player = {
      id: this.newId(),
      name: cleaned,
      rating: this.clamp(rating),
      active: true,
      benched: false
    };

    this.players.update((list) => [...list, player]);
    this.invalidate();
    return player;
  }

  addMany(names: { name: string; rating: number }[]): number {
    const created = names
      .map(({ name, rating }) => ({
        id: this.newId(),
        name: name.trim().replace(/\s+/g, ' '),
        rating: this.clamp(rating),
        active: true,
        benched: false
      }))
      .filter((p) => p.name.length > 0);

    if (created.length === 0) return 0;
    this.players.update((list) => [...list, ...created]);
    this.invalidate();
    return created.length;
  }

  rename(id: string, name: string): void {
    const cleaned = name.trim().replace(/\s+/g, ' ');
    if (!cleaned) return;
    this.players.update((list) => list.map((p) => (p.id === id ? { ...p, name: cleaned } : p)));
    this.invalidate();
  }

  setRating(id: string, rating: number): void {
    const value = this.clamp(rating);
    this.players.update((list) => list.map((p) => (p.id === id ? { ...p, rating: value } : p)));
    this.invalidate();
  }

  /**
   * Repasser un joueur en présent le remet en jeu: garder un banc posé avant
   * son absence le ferait revenir remplaçant sans que personne l'ait décidé.
   */
  toggleActive(id: string): void {
    this.players.update((list) =>
      list.map((p) => {
        if (p.id !== id) return p;
        const active = !p.active;
        return { ...p, active, benched: active ? false : p.benched };
      })
    );
    this.invalidate();
  }

  /** Le banc n'a de sens que pour un présent: bascule volontaire, jamais subie. */
  toggleBenched(id: string): void {
    this.players.update((list) =>
      list.map((p) => (p.id === id ? { ...p, benched: !p.benched } : p))
    );
    this.invalidate();
  }

  /**
   * « Tout le monde joue » doit vraiment remettre tout le monde en jeu: garder
   * un banc après avoir tout marqué présent serait incompréhensible.
   */
  setAllActive(active: boolean): void {
    this.players.update((list) =>
      list.map((p) => ({ ...p, active, benched: active ? false : p.benched }))
    );
    this.invalidate();
  }

  remove(id: string): RemovedPlayer | null {
    const index = this.players().findIndex((p) => p.id === id);
    if (index === -1) return null;

    const player = this.players()[index];
    this.players.update((list) => list.filter((p) => p.id !== id));
    this.invalidate();
    return { player, index };
  }

  /** Réinsère un joueur supprimé à sa place d'origine (annulation). */
  restore(removed: RemovedPlayer): void {
    this.players.update((list) => {
      const next = [...list];
      next.splice(Math.min(removed.index, next.length), 0, removed.player);
      return next;
    });
    this.invalidate();
  }

  clear(): void {
    this.players.set([]);
    this.composition.set(null);
    this.compositionStale.set(false);
  }

  setRatingMode(mode: RatingMode): void {
    // Les notes sont canoniques (0-100): changer de mode ne touche pas aux données.
    this.ratingMode.set(mode);
  }

  setTeamSize(size: number | null): void {
    this.teamSize.set(size);
    this.invalidate();
  }

  setComposition(composition: TeamComposition | null): void {
    this.composition.set(composition);
    this.compositionStale.set(false);
  }

  /**
   * Affiche une composition reçue par URL. L'effectif local n'est repris du
   * lien que s'il est vide: ouvrir le lien d'un ami ne doit pas effacer sa
   * propre liste de joueurs.
   */
  adoptShared(composition: TeamComposition): void {
    // Les liens émis avant les remplaçants n'ont pas de banc.
    const shared: TeamComposition = { ...composition, bench: composition.bench ?? [] };

    if (this.players().length === 0) {
      this.players.set([
        ...[...shared.teamA, ...shared.teamB].map((p) =>
          this.normalize({ ...p, active: true, benched: false })
        ),
        ...shared.bench.map((p) => this.normalize({ ...p, active: true, benched: true }))
      ]);
    }

    this.composition.set(shared);
    this.compositionStale.set(false);
  }

  // ------------------------------------------------------------------ interne

  private invalidate(): void {
    if (this.composition()) this.compositionStale.set(true);
  }

  private clamp(rating: number): number {
    if (!Number.isFinite(rating)) return 50;
    return Math.min(100, Math.max(0, Math.round(rating)));
  }

  private newId(): string {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
    return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  }

  private hydrate(): void {
    if (!this.isBrowser) return;

    try {
      const mode = localStorage.getItem(KEY_MODE);
      if (mode === 'STARS' || mode === 'SCORE') this.ratingMode.set(mode);

      const size = Number.parseInt(localStorage.getItem(KEY_TEAM_SIZE) ?? '', 10);
      if (Number.isFinite(size) && size > 0) this.teamSize.set(size);

      const raw = localStorage.getItem(KEY_PLAYERS);
      if (raw) {
        const parsed = JSON.parse(raw) as Player[];
        if (Array.isArray(parsed)) {
          this.players.set(parsed.map((p) => this.normalize(p)));
        }
        return;
      }

      this.migrateLegacy();
    } catch {
      // Données illisibles: on repart d'un effectif vide plutôt que de planter.
    }
  }

  /**
   * v1 stockait la note dans l'échelle affichée. En mode étoiles, les notes
   * valaient 1-5 et doivent être remises sur l'échelle canonique 0-100.
   */
  private migrateLegacy(): void {
    const raw = localStorage.getItem(LEGACY_KEY_PLAYERS);
    if (!raw) return;

    const parsed = JSON.parse(raw) as Player[];
    if (!Array.isArray(parsed)) return;

    const legacyMode = localStorage.getItem(LEGACY_KEY_MODE);
    const storedAsStars = legacyMode === 'STARS' && parsed.every((p) => (p?.rating ?? 0) <= 5);

    this.players.set(
      parsed.map((p) =>
        this.normalize({ ...p, rating: storedAsStars ? (p?.rating ?? 3) * 20 : p?.rating })
      )
    );
    this.ratingMode.set(legacyMode === 'STARS' ? 'STARS' : 'SCORE');
  }

  private normalize(player: Partial<Player>): Player {
    return {
      id: player.id || this.newId(),
      name: (player.name || '').trim() || '?',
      rating: this.clamp(player.rating ?? 50),
      active: player.active !== false,
      // Absent des effectifs enregistrés avant les remplaçants: tout le monde joue.
      benched: player.benched === true
    };
  }
}
